import React, {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApiService } from '../../services/apiService';
import {
  advancePostUpdateJourneyStepForProduct,
  getPostUpdateJourneyStepForProduct,
  isFullUpdateSpotlightCompleted,
  PostUpdateJourneyStep,
  setPostUpdateJourneyStepForProduct,
} from '../onboarding/onboardingState';

export type PostUpdateJourneyContextValue = {
  /** Active step for UI, or null when journey is not running */
  currentStep: PostUpdateJourneyStep | null;
  /** True when a step is active (not completed/skipped) */
  isJourneyActive: boolean;
  advance: () => void;
  skip: () => void;
  dismissCurrentStep: () => void;
  /** Header nav path to pulse during navigation steps */
  navHighlightPath: string | null;
  /** Spotlight must finish before journey; done count from API */
  journeyEligible: boolean;
};

const PostUpdateJourneyContext = createContext<PostUpdateJourneyContextValue | null>(null);

export function PostUpdateJourneyProvider({ children }: { children: React.ReactNode }) {
  const { currentOrg, currentProduct } = useAuth();
  const { pathname } = useLocation();
  const orgId = currentOrg?.id;
  const productId = currentProduct?.id;
  const defaultProductIdForLegacyMigration =
    currentOrg?.products?.find((p) => p.is_default)?.id ?? currentOrg?.products?.[0]?.id ?? null;

  const [doneCount, setDoneCount] = useState(0);
  const [doneCountLoaded, setDoneCountLoaded] = useState(false);
  const [tick, setTick] = useState(0);

  const bump = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!orgId) {
      setDoneCountLoaded(false);
      setDoneCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      setDoneCountLoaded(false);
      try {
        const { totalDoneIssues } = await ApiService.getTotalDoneIssues();
        if (!cancelled) setDoneCount(totalDoneIssues);
      } catch {
        if (!cancelled) setDoneCount(0);
      } finally {
        if (!cancelled) setDoneCountLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, currentProduct?.id]);

  const spotlightCompleted = isFullUpdateSpotlightCompleted();

  const journeyEligible = Boolean(
    orgId && productId && spotlightCompleted && doneCountLoaded && doneCount > 0
  );

  const persistedStep = useMemo(() => {
    if (!orgId || !productId) return undefined;
    void tick;
    return getPostUpdateJourneyStepForProduct(orgId, productId, defaultProductIdForLegacyMigration);
  }, [orgId, productId, defaultProductIdForLegacyMigration, tick]);

  // Initialize journey on first eligible visit to Analytics
  useEffect(() => {
    if (!orgId || !productId || !journeyEligible) return;
    if (persistedStep !== undefined) return;
    if (pathname !== '/analytics') return;
    setPostUpdateJourneyStepForProduct(orgId, productId, 'dateFilter');
    bump();
  }, [orgId, productId, journeyEligible, pathname, persistedStep, bump]);

  // Auto-advance when landing on Calendar after "Open Calendar"
  useEffect(() => {
    if (!orgId || !productId || !journeyEligible) return;
    const step = getPostUpdateJourneyStepForProduct(
      orgId,
      productId,
      defaultProductIdForLegacyMigration
    );
    if (step === 'navigateCalendar' && pathname === '/calendar') {
      setPostUpdateJourneyStepForProduct(orgId, productId, 'navigateReleases');
      bump();
    }
  }, [orgId, productId, journeyEligible, pathname, defaultProductIdForLegacyMigration, bump]);

  // Complete when landing on Releases
  useEffect(() => {
    if (!orgId || !productId || !journeyEligible) return;
    const step = getPostUpdateJourneyStepForProduct(
      orgId,
      productId,
      defaultProductIdForLegacyMigration
    );
    if (step === 'navigateReleases' && pathname === '/releases') {
      setPostUpdateJourneyStepForProduct(orgId, productId, 'completed');
      bump();
    }
  }, [orgId, productId, journeyEligible, pathname, defaultProductIdForLegacyMigration, bump]);

  const currentStep = useMemo((): PostUpdateJourneyStep | null => {
    if (!orgId || !productId || !journeyEligible) return null;
    void tick;
    const step = getPostUpdateJourneyStepForProduct(
      orgId,
      productId,
      defaultProductIdForLegacyMigration
    );
    if (step === undefined) return null;
    if (step === 'completed' || step === 'skipped') return null;
    return step;
  }, [orgId, productId, journeyEligible, defaultProductIdForLegacyMigration, tick]);

  const advance = useCallback(() => {
    if (!orgId || !productId) return;
    advancePostUpdateJourneyStepForProduct(orgId, productId, defaultProductIdForLegacyMigration);
    bump();
  }, [orgId, productId, bump, defaultProductIdForLegacyMigration]);

  const skip = useCallback(() => {
    if (!orgId || !productId) return;
    setPostUpdateJourneyStepForProduct(orgId, productId, 'skipped');
    bump();
  }, [orgId, productId, bump]);

  const dismissCurrentStep = useCallback(() => {
    advance();
  }, [advance]);

  const navHighlightPath = useMemo(() => {
    if (!currentStep) return null;
    if (currentStep === 'navigateCalendar') return '/calendar';
    if (currentStep === 'navigateReleases') return '/releases';
    return null;
  }, [currentStep]);

  const value = useMemo(
    (): PostUpdateJourneyContextValue => ({
      currentStep,
      isJourneyActive: currentStep !== null,
      advance,
      skip,
      dismissCurrentStep,
      navHighlightPath,
      journeyEligible,
    }),
    [currentStep, advance, skip, dismissCurrentStep, navHighlightPath, journeyEligible]
  );

  return createElement(PostUpdateJourneyContext.Provider, { value }, children);
}

export function usePostUpdateJourney(): PostUpdateJourneyContextValue {
  const ctx = useContext(PostUpdateJourneyContext);
  if (!ctx) {
    throw new Error('usePostUpdateJourney must be used within PostUpdateJourneyProvider');
  }
  return ctx;
}

/** Safe for Navigation when provider might be absent (tests). */
export function usePostUpdateJourneyOptional(): PostUpdateJourneyContextValue {
  const ctx = useContext(PostUpdateJourneyContext);
  if (ctx) return ctx;
  return {
    currentStep: null,
    isJourneyActive: false,
    advance: () => {},
    skip: () => {},
    dismissCurrentStep: () => {},
    navHighlightPath: null,
    journeyEligible: false,
  };
}
